
import React from 'react';
import ReactMarkdown from 'react-markdown';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

interface HybridRendererProps {
  text: string;
}

interface TextSegment {
  content: string;
  type: 'text' | 'math' | 'inline-math';
}

const HybridRenderer: React.FC<HybridRendererProps> = ({ text }) => {
  // Enhanced detection for invalid math placeholders
  const hasInvalidPlaceholders = /MATH_PLACEHOLDER_\d+/.test(text);
  if (hasInvalidPlaceholders) {
    console.error('🚨 CRITICAL: Invalid math placeholders detected in text:', text);
    console.error('Expected LaTeX format: $$\\text{Formula} = \\frac{\\text{Num}}{\\text{Denom}}$$');
  }

  // Function to convert LaTeX text notation to proper LaTeX (borrowed from MathRenderer)
  const preprocessLatex = (latex: string): string => {
    return latex
      // Convert \text{...} to \mathrm{...} for better rendering
      .replace(/\\text\{([^}]+)\}/g, '\\mathrm{$1}')
      // Ensure proper spacing around operators
      .replace(/\\times/g, ' \\times ')
      .replace(/=/g, ' = ')
      // Clean up extra spaces
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Function to preprocess text and convert common patterns to markdown
  const preprocessTextToMarkdown = (inputText: string): string => {
    let processedText = inputText;
    
    // Convert numbered list patterns like "1. **Text**:" to proper markdown
    processedText = processedText.replace(/^(\d+)\.\s*\*\*([^*]+)\*\*:\s*/gm, '$1. **$2:**\n\n');
    
    // Fix "all 1." numbered lists - a common issue where AI outputs all items as "1."
    // Split into lines and track/renumber only if we detect the "all 1s" pattern
    const lines = processedText.split('\n');
    
    // First pass: detect if this is an "all 1s" list that needs fixing
    const numberedLineInfo: { index: number; indentation: string; content: string; originalNum: number }[] = [];
    
    lines.forEach((line, index) => {
      const match = line.match(/^(\s*)(\d+)\.\s+(.+)/);
      if (match) {
        numberedLineInfo.push({
          index,
          indentation: match[1],
          content: match[3],
          originalNum: parseInt(match[2])
        });
      }
    });
    
    // Check if all numbered items are "1." (the common AI bug)
    const allOnes = numberedLineInfo.length > 1 && 
      numberedLineInfo.every(info => info.originalNum === 1);
    
    // Check if numbers are already sequential (no fix needed)
    const alreadySequential = numberedLineInfo.length > 1 &&
      numberedLineInfo.every((info, i) => info.originalNum === i + 1);
    
    // Only renumber if we have the "all 1s" problem
    if (allOnes && !alreadySequential) {
      // Group numbered items by indentation level to handle nested lists
      const indentGroups = new Map<string, number>();
      
      const fixedLines = [...lines];
      
      numberedLineInfo.forEach((info) => {
        // Get or initialize counter for this indentation level
        const currentCount = (indentGroups.get(info.indentation) || 0) + 1;
        indentGroups.set(info.indentation, currentCount);
        
        // Replace the line with correct numbering
        fixedLines[info.index] = `${info.indentation}${currentCount}. ${info.content}`;
      });
      
      processedText = fixedLines.join('\n');
    }
    
    // Convert bullet-like patterns with dashes to proper markdown bullets  
    processedText = processedText.replace(/^[\s]*-\s+([^\n]+)/gm, '- $1');
    
    // Ensure proper line breaks before numbered lists (but don't affect inline content)
    processedText = processedText.replace(/([^\n])\n(\d+\.\s+)/g, '$1\n\n$2');
    
    // Ensure double line breaks after bold headers followed by content
    processedText = processedText.replace(/(\*\*[^*]+\*\*)\n([^\n\s])/g, '$1\n\n$2');
    
    return processedText;
  };

  // Check if text has markdown formatting
  const hasMarkdownFormatting = (inputText: string): boolean => {
    const markdownPatterns = [
      /\*\*[^*]+\*\*/g, // Bold text
      /^\s*[\-\*]\s+/m, // Bullet points
      /^\d+\.\s+/m, // Numbered lists
      /^#{1,6}\s+/m, // Headers
      /`[^`]+`/g, // Inline code
      /```[\s\S]*?```/g // Code blocks
    ];
    
    return markdownPatterns.some(pattern => pattern.test(inputText));
  };

  // Check if text has math expressions
  const hasMathExpressions = (inputText: string): boolean => {
    const mathPatterns = [
      /\$\$([^$]+)\$\$/g, // Display math
      /\$([^$\n]+)\$/g, // Inline math with LaTeX commands
      /\[([^\]]*\\[a-zA-Z]+[^\]]*)\]/g, // Bracket math with LaTeX
      /\\([^\\]*?\\[a-zA-Z]+[^\\]*?)\\/ // Backslash math
    ];
    
    return mathPatterns.some(pattern => {
      const matches = inputText.match(pattern);
      if (!matches) return false;
      // For inline math, only count if it contains LaTeX commands
      if (pattern.source.includes('\\$([^$\\n]+)\\$')) {
        return matches.some(match => /\\[a-zA-Z]+/.test(match));
      }
      return true;
    });
  };

  // Handle invalid placeholders with enhanced error message
  if (hasInvalidPlaceholders) {
    return (
      <div className="border-l-4 border-amber-400 bg-amber-50 p-4 rounded-r">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-amber-800">Mathematical Expression Formatting Issue</h3>
            <div className="mt-2 text-sm text-amber-700">
              <p>The AI response contains placeholder text instead of properly formatted mathematical expressions.</p>
              <p className="mt-1 font-medium">Expected format example:</p>
              <code className="block mt-1 p-2 bg-amber-100 rounded text-xs">
                Repeat Customer Rate = (Customers with &gt;1 Order / Total Customers) × 100%
              </code>
            </div>
          </div>
        </div>
        <div className="mt-4 border-t border-amber-200 pt-4">
          <div className="text-sm text-amber-800">
            <strong>Response content:</strong>
          </div>
          <div className="mt-2 whitespace-pre-wrap text-sm text-gray-700 bg-white p-3 rounded border">
            {text}
          </div>
        </div>
      </div>
    );
  }

  // Function to split text into segments of math and non-math content
  const segmentText = (inputText: string): TextSegment[] => {
    const segments: TextSegment[] = [];
    let currentText = inputText;
    
    // Process in order: display math, inline math (both $, [], and \), then remaining text
    
    // First, handle display math with $$ delimiters
    const displayMathRegex = /\$\$([^$]+)\$\$/g;
    let lastIndex = 0;
    let match;
    
    while ((match = displayMathRegex.exec(currentText)) !== null) {
      // Add text before the math
      if (match.index > lastIndex) {
        const beforeText = currentText.slice(lastIndex, match.index);
        if (beforeText.trim()) {
          segments.push({ content: beforeText, type: 'text' });
        }
      }
      
      // Add the math expression
      segments.push({ 
        content: match[1], 
        type: 'math' 
      });
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text after all display math
    if (lastIndex < currentText.length) {
      const remainingText = currentText.slice(lastIndex);
      
      // Now process the remaining text for inline math (both $, [], and \)
      const inlineMathSegments = processInlineMath(remainingText);
      segments.push(...inlineMathSegments);
    }
    
    return segments;
  };

  // Function to process inline math in text (both $, [], and \ delimiters)
  const processInlineMath = (text: string): TextSegment[] => {
    const segments: TextSegment[] = [];
    let currentText = text;
    let globalLastIndex = 0;
    
    // Enhanced regex to find $...$, [...], and \...\ patterns with improved \...\ handling
    const combinedMathRegex = /(\$([^$\n]+)\$|\[([^\]]*\\[a-zA-Z]+[^\]]*)\]|\\([^\\]*?\\[a-zA-Z]+[^\\]*?)\\)/g;
    let match;
    
    while ((match = combinedMathRegex.exec(currentText)) !== null) {
      // Add text before the math
      if (match.index > globalLastIndex) {
        const beforeText = currentText.slice(globalLastIndex, match.index);
        if (beforeText.trim()) {
          segments.push({ content: beforeText, type: 'text' });
        }
      }
      
      // Determine which pattern matched and extract the math content
      let mathContent = '';
      if (match[2]) {
        // $...$ pattern matched
        // Only treat as math if it contains LaTeX commands
        if (/\\[a-zA-Z]+/.test(match[2])) {
          mathContent = match[2];
        }
      } else if (match[3]) {
        // [...] pattern matched (already filtered for LaTeX commands in regex)
        mathContent = match[3];
      } else if (match[4]) {
        // \...\ pattern matched - clean up whitespace and newlines
        mathContent = match[4].replace(/\s+/g, ' ').trim();
      }
      
      if (mathContent) {
        // Add the inline math expression
        segments.push({ 
          content: mathContent, 
          type: 'inline-math' 
        });
      } else {
        // Not actually math, treat as regular text
        segments.push({ content: match[0], type: 'text' });
      }
      
      globalLastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (globalLastIndex < currentText.length) {
      const remainingText = currentText.slice(globalLastIndex);
      if (remainingText.trim()) {
        segments.push({ content: remainingText, type: 'text' });
      }
    }
    
    // If no math was found, return the original text as a single segment
    if (segments.length === 0 && text.trim()) {
      segments.push({ content: text, type: 'text' });
    }
    
    return segments;
  };

  // Main rendering function
  const renderHybridContent = (inputText: string) => {
    // First preprocess the text to convert common patterns to proper markdown
    const preprocessedText = preprocessTextToMarkdown(inputText);
    
    // Check for math and markdown presence in preprocessed text
    const hasMath = hasMathExpressions(preprocessedText);
    const hasMarkdown = hasMarkdownFormatting(preprocessedText);
        
    // If no math or markdown, render as plain text
    if (!hasMath && !hasMarkdown) {
      return <div className="whitespace-pre-wrap">{preprocessedText}</div>;
    }
    
    // If only markdown (no math), use ReactMarkdown directly
    if (!hasMath && hasMarkdown) {
      return (
        <div className="prose prose-sm max-w-none prose-headings:text-blue-900 prose-p:my-2 prose-ul:my-2 prose-li:my-0">
          <ReactMarkdown 
            components={{
              ul: ({ children }) => <ul className="list-disc ml-4 space-y-1 my-2">{children}</ul>,
              ol: ({ children }) => <ol className="list-decimal ml-4 space-y-1 my-2">{children}</ol>,
              li: ({ children }) => <li className="leading-relaxed">{children}</li>,
              p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
              strong: ({ children }) => <strong className="font-semibold text-blue-800">{children}</strong>,
              code: ({ children }) => <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">{children}</code>,
              em: ({ children }) => <em className="italic">{children}</em>
            }}
          >
            {preprocessedText}
          </ReactMarkdown>
        </div>
      );
    }
    
    // If has math (with or without markdown), use segmentation approach
    const segments = segmentText(preprocessedText);
    
    // Render segments with both math and markdown
    return (
      <div>
        {segments.map((segment, index) => {
          if (segment.type === 'math') {
            try {
              return (
                <div key={index} className="my-2 text-center">
                  <BlockMath math={preprocessLatex(segment.content)} />
                </div>
              );
            } catch (error) {
              console.error('KaTeX rendering error:', error);
              return (
                <div key={index} className="my-2 p-2 bg-gray-100 rounded text-sm">
                  $${segment.content}$$
                </div>
              );
            }
          }
          
          if (segment.type === 'inline-math') {
            try {
              return (
                <InlineMath key={index} math={preprocessLatex(segment.content)} />
              );
            } catch (error) {
              console.error('KaTeX rendering error:', error);
              return (
                <span key={index} className="p-1 bg-gray-100 rounded text-sm">
                  {segment.content}
                </span>
              );
            }
          }
          
          // For text segments, use ReactMarkdown if it has markdown formatting
          if (hasMarkdownFormatting(segment.content)) {
            return (
              <span key={index} style={{ whiteSpace: 'pre-wrap' }}>
                <ReactMarkdown 
                  components={{
                    p: ({ children }) => <span>{children}</span>,
                    strong: ({ children }) => <strong className="font-semibold text-blue-800">{children}</strong>,
                    em: ({ children }) => <em className="italic">{children}</em>,
                    code: ({ children }) => <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">{children}</code>,
                    ul: ({ children }) => <ul className="list-disc ml-4 space-y-1 my-2">{children}</ul>,
                    ol: ({ children }) => <ol className="list-decimal ml-4 space-y-1 my-2">{children}</ol>,
                    li: ({ children }) => <li className="leading-relaxed">{children}</li>,
                  }}
                >
                  {segment.content}
                </ReactMarkdown>
              </span>
            );
          }
          
          // Plain text - preserve whitespace
          return <span key={index} style={{ whiteSpace: 'pre-wrap' }}>{segment.content}</span>;
        })}
      </div>
    );
  };

  return renderHybridContent(text);
};

export default HybridRenderer;
